import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { translate } from './translateFunction';
import './UserRequest.css'; // CSS-Datei für den Ladebalken

const UserRequest = () => {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [editingRequest, setEditingRequest] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [company, setCompany] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [showProgressBar, setShowProgressBar] = useState(false);

  const [translations, setTranslations] = useState({
    openFeedbackRequests: '',
    sentOn: '',
    customerCompany: '',
    contactPerson: '',
    emailAddress: '',
    feedbackUrl: '',
    actions: '',
    noRequestsFound: '',
    editButton: '',
    deleteButton: '',
    confirmDeleteButton: '',
    remindButton: '',
    saveButton: '',
    cancelButton: '',
    editRequestTitle: '',
  });

  useEffect(() => {
    const loadTranslations = async () => {
      const openFeedbackRequests = await translate(190);
      const sentOn = await translate(191);
      const customerCompany = await translate(192);
      const contactPerson = await translate(193);
      const emailAddress = await translate(194);
      const feedbackUrl = await translate(195);
      const actions = await translate(196);
      const noRequestsFound = await translate(197);
      const editButton = await translate(198);
      const deleteButton = await translate(199);
      const confirmDeleteButton = await translate(200);
      const remindButton = await translate(201);
      const saveButton = await translate(202);
      const cancelButton = await translate(203);
      const editRequestTitle = await translate(204);

      setTranslations({
        openFeedbackRequests,
        sentOn,
        customerCompany,
        contactPerson,
        emailAddress,
        feedbackUrl,
        actions,
        noRequestsFound,
        editButton,
        deleteButton,
        confirmDeleteButton,
        remindButton,
        saveButton,
        cancelButton,
        editRequestTitle,
      });
    };

    loadTranslations();
  }, []);

  useEffect(() => {
    const fetchRequests = async () => {
      const token = localStorage.getItem('token');
      if (token && user && user.id) {
        try {
          const response = await axios.get(`/api/customerFeedback/user/${user.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setRequests(response.data || []);
        } catch (error) {
          console.error('Error fetching feedback requests:', error);
          setRequests([]);
        }
      }
    };

    fetchRequests();
  }, [user]);

  const handleEdit = (request) => {
    setEditingRequest(request);
    setActiveRow(request.customerFdbckID);
    setCompany(request.customerCompany);
    setContactPerson(request.customerName);
    setEmail(request.customerMailaddr);
  };

  const handleSaveRequest = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `/api/customerFeedback/${editingRequest.customerFdbckID}`,
        {
          customerCompany: company,
          customerName: contactPerson,
          customerMailaddr: email,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setRequests(
        requests.map((req) =>
          req.customerFdbckID === editingRequest.customerFdbckID
            ? { ...req, customerCompany: company, customerName: contactPerson, customerMailaddr: email }
            : req
        )
      );
      setEditingRequest(null);
      setActiveRow(null);
      setMessage(translations.saveButton);
    } catch (error) {
      console.error('Error saving feedback request:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingRequest(null);
    setActiveRow(null);
  };

  const handleDeleteRequest = async (requestID) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/customerFeedback/${requestID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(requests.filter((req) => req.customerFdbckID !== requestID));
      setDeletingRequestId(null);
    } catch (error) {
      console.error('Error deleting feedback request:', error);
    }
  };

  const confirmDelete = (requestID) => {
    setDeletingRequestId(requestID);
  };

  const cancelDelete = () => {
    setDeletingRequestId(null);
  };

  const handleRemind = async (request) => {
    setShowProgressBar(true);
    setActiveRow(request.customerFdbckID);

    try {
      // Der Server schickt den Reminder an den Ersteller der Anfrage, der ihn an den Kunden weiterleitet
      await axios.post(`/api/customerFeedback/${request.customerFdbckID}/reminder`);

      setMessage('Reminder email sent successfully.');
    } catch (error) {
      console.error('Error sending reminder email:', error);
      setMessage('Failed to send reminder email.');
    } finally {
      setTimeout(() => {
        setShowProgressBar(false);
        setActiveRow(null);
      }, 2000);
    }
  };

  return (
    <div className="mb-5">
      {showProgressBar && <div className="progress-bar"></div>}
      <h3>{translations.openFeedbackRequests}</h3>
      {editingRequest && (
        <div className="mt-5">
          <h3>{translations.editRequestTitle}</h3>
          <div className="form-group">
            <label>{translations.customerCompany}</label>
            <input
              type="text"
              className="form-control"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>{translations.contactPerson}</label>
            <input
              type="text"
              className="form-control"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>{translations.emailAddress}</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={handleSaveRequest}>
            {translations.saveButton}
          </button>
          <button className="btn btn-secondary ml-2" onClick={handleCancelEdit}>
            {translations.cancelButton}
          </button>
        </div>
      )}
      {message && <p>{message}</p>}
      <table className="table">
        <thead>
          <tr>
            <th>{translations.sentOn}</th>
            <th>{translations.customerCompany}</th>
            <th>{translations.contactPerson}</th>
            <th>{translations.emailAddress}</th>
            <th>{translations.feedbackUrl}</th>
            <th>{translations.actions}</th>
          </tr>
        </thead>
        <tbody>
          {requests.length > 0 ? (
            requests.map((request) => (
              <tr
                key={request.customerFdbckID}
                className={activeRow === request.customerFdbckID ? 'active' : ''}
              >
                <td>{new Date(request.customerFdbckSend).toLocaleDateString("fr-CH")}</td>
                <td>{request.customerCompany}</td>
                <td>{request.customerName}</td>
                <td>{request.customerMailaddr}</td>
                <td>
                  <a href={request.customerFdbckUrl} target="_blank" rel="noreferrer">
                    {translations.feedbackUrl}
                  </a>
                </td>
                <td>
                  {deletingRequestId === request.customerFdbckID ? (
                    <>
                      <button
                        className="btn btn-danger mr-2"
                        onClick={() => handleDeleteRequest(request.customerFdbckID)}
                      >
                        {translations.confirmDeleteButton}
                      </button>
                      <button className="btn btn-secondary" onClick={cancelDelete}>
                        {translations.cancelButton}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-primary mr-2"
                        onClick={() => handleEdit(request)}
                      >
                        {translations.editButton}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => confirmDelete(request.customerFdbckID)}
                      >
                        {translations.deleteButton}
                      </button>
                      <button
                        className="btn btn-warning"
                        onClick={() => handleRemind(request)}
                      >
                        {translations.remindButton}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6">{translations.noRequestsFound}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UserRequest;
